// covered-by: frontends/carrie/src/ask-v2/home/__tests__/AskV2MentionTasks.test.tsx
import { CaretLeft, CaretRight, ChatCircleText } from "@phosphor-icons/react";
import { Button, Heading, IconButton, Text } from "@radix-ui/themes";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { type AskV2MentionTask } from "./mentionTasks";
import { useAskV2MentionTasks } from "./useAskV2MentionTasks";

import {
  formatListCardRelativeDate,
  LIST_CARD_RELATIVE_TIME_CLASS,
} from "../../components/ask-puntt-list/listCardTimestamps";
import { useAnalytics } from "../../utils/analytics";
import { useMarkAskPunttChatRead } from "../../utils/queries/askPuntt";
import { askV2ChatNavLink } from "../shell/askV2ChatNavigation";

export const ASK_V2_MENTION_TASKS_PAGE_SIZE = 3;
const TITLE_ID = "ask-v2-mention-tasks-title";

export function mentionTaskRowTestId(task: AskV2MentionTask): string {
  return `ask-v2-mention-task-${task.chatId}`;
}

export function mentionTaskOpenTestId(task: AskV2MentionTask): string {
  return `ask-v2-mention-task-open-${task.chatId}`;
}

function MentionTaskRow({
  task,
  onOpen,
  onMarkedRead,
}: {
  task: AskV2MentionTask;
  onOpen: (task: AskV2MentionTask) => void;
  onMarkedRead: () => void;
}) {
  const { t } = useTranslation("ui");
  const markRead = useMarkAskPunttChatRead(task.chatId);
  const title = task.title ?? t("askV2.home.tasks.fallbackTitle");
  const relative = formatListCardRelativeDate(task.createdAt || undefined);
  const rowTestId = mentionTaskRowTestId(task);
  const open = () => {
    // why: the chat read receipt is what clears the mention on the server; the
    // V2 chat page also sends one on open, but this row must clear even when
    // the navigation is interrupted, so fire it here before leaving.
    // why mutateAsync: `mutate`'s per-call callbacks are dropped when the
    // observer loses its listeners, and `onOpen` unmounts this row on the very
    // next render, so an `onSettled` option would never run and the cached
    // profile would keep serving the mention as unread.
    void markRead.mutateAsync(undefined).then(onMarkedRead, onMarkedRead);
    onOpen(task);
  };

  return (
    <li
      data-testid={rowTestId}
      data-chat-id={task.chatId}
      className="flex items-center gap-3 rounded-xl border border-puntt-neutral-gray-7 bg-base-white p-2 pr-3 shadow-[0_2px_3px_rgba(34,34,1,0.06),0_3px_12px_rgba(23,26,36,0.02)]"
    >
      <button
        type="button"
        className="flex min-w-0 flex-1 cursor-pointer items-center gap-3 rounded-lg p-1 text-left outline-none focus-visible:ring-2 focus-visible:ring-puntt-accent-8"
        data-testid={`${rowTestId}-title`}
        onClick={open}
      >
        <span
          aria-hidden="true"
          data-testid={`${rowTestId}-icon`}
          className="flex size-12 shrink-0 items-center justify-center rounded-md bg-puntt-blue-3 text-puntt-blue-11"
        >
          <ChatCircleText size={22} weight="fill" />
        </span>
        <span className="min-w-0 flex-1">
          <Text
            as="p"
            size="2"
            weight="medium"
            className="truncate text-puntt-neutral-gray-12"
          >
            {title}
          </Text>
          <span className="mt-1 flex flex-wrap items-center gap-2">
            <span
              className="inline-block rounded-sm bg-puntt-neutral-gray-3 px-2 py-0.5 text-[12px] font-medium text-puntt-neutral-gray-11"
              data-testid={`${rowTestId}-kind`}
            >
              {t("askV2.home.tasks.mention")}
            </span>
            {relative ? (
              <span
                className={LIST_CARD_RELATIVE_TIME_CLASS}
                data-testid={`${rowTestId}-time`}
              >
                {relative}
              </span>
            ) : null}
          </span>
        </span>
      </button>
      <Button
        type="button"
        size="2"
        variant="outline"
        color="gray"
        data-testid={mentionTaskOpenTestId(task)}
        aria-label={t("askV2.home.tasks.openChatFor", { title })}
        onClick={open}
      >
        {t("askV2.home.tasks.openChat")}
      </Button>
    </li>
  );
}

/**
 * "Tasks/ToDos" on the V2 home: one row per chat where someone @mentioned the
 * reader and they have not opened it yet. Opening a row marks the chat read
 * (which clears the mention server-side) and lands in that chat so the
 * question can be answered. Renders nothing when nothing is outstanding.
 */
export function AskV2MentionTasks() {
  const { t } = useTranslation("ui");
  const { tasks: openTasks } = useAskV2MentionTasks();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const analytics = useAnalytics("ask_puntt_v2_home");
  // why: keyed by task id (chat id + mention timestamp), not chat id — the
  // dismissal only hides the mention that was just opened. A later @mention in
  // the same chat is a new task and must surface on the next poll instead of
  // staying buried for the rest of the session.
  const [dismissedTaskIds, setDismissedTaskIds] = useState<ReadonlySet<string>>(
    () => new Set(),
  );
  const [page, setPage] = useState(0);

  const tasks = useMemo(
    () => openTasks.filter((task) => !dismissedTaskIds.has(task.id)),
    [dismissedTaskIds, openTasks],
  );
  const pageCount = Math.max(
    1,
    Math.ceil(tasks.length / ASK_V2_MENTION_TASKS_PAGE_SIZE),
  );

  useEffect(() => {
    setPage((current) => Math.min(current, pageCount - 1));
  }, [pageCount]);

  if (tasks.length === 0) return null;

  const start = page * ASK_V2_MENTION_TASKS_PAGE_SIZE;
  const visibleTasks = tasks.slice(
    start,
    start + ASK_V2_MENTION_TASKS_PAGE_SIZE,
  );

  const handleOpen = (task: AskV2MentionTask) => {
    analytics.capture("ask_puntt_mention_task_opened", {
      chat_id: task.chatId,
      project_id: task.projectId ?? undefined,
    });
    setDismissedTaskIds((previous) => new Set(previous).add(task.id));
    void navigate(
      askV2ChatNavLink({ _id: task.chatId, projectId: task.projectId }),
    );
  };

  const handleMarkedRead = () => {
    void queryClient.invalidateQueries({ queryKey: ["user-profile"] });
  };

  return (
    <section
      data-testid="ask-v2-mention-tasks"
      data-task-count={tasks.length}
      aria-labelledby={TITLE_ID}
      className="w-full"
    >
      <div className="mb-3 flex items-center justify-between gap-3">
        <Heading
          as="h2"
          id={TITLE_ID}
          size="4"
          weight="bold"
          className="text-puntt-neutral-gray-12"
          data-testid={TITLE_ID}
        >
          {t("askV2.home.tasks.title")}
        </Heading>
        {pageCount > 1 ? (
          <div
            className="flex items-center gap-2"
            data-testid="ask-v2-mention-tasks-pager"
            data-page={page + 1}
            data-page-count={pageCount}
          >
            <IconButton
              type="button"
              size="2"
              variant="outline"
              color="gray"
              data-testid="ask-v2-mention-tasks-prev"
              aria-label={t("askV2.home.tasks.previous")}
              disabled={page === 0}
              onClick={() => setPage((current) => Math.max(0, current - 1))}
            >
              <CaretLeft size={16} aria-hidden="true" />
            </IconButton>
            <IconButton
              type="button"
              size="2"
              variant="outline"
              color="gray"
              data-testid="ask-v2-mention-tasks-next"
              aria-label={t("askV2.home.tasks.next")}
              disabled={page >= pageCount - 1}
              onClick={() =>
                setPage((current) => Math.min(pageCount - 1, current + 1))
              }
            >
              <CaretRight size={16} aria-hidden="true" />
            </IconButton>
          </div>
        ) : null}
      </div>
      <ul
        className="flex list-none flex-col gap-3 p-0"
        data-testid="ask-v2-mention-tasks-list"
      >
        {visibleTasks.map((task) => (
          <MentionTaskRow
            key={task.chatId}
            task={task}
            onOpen={handleOpen}
            onMarkedRead={handleMarkedRead}
          />
        ))}
      </ul>
    </section>
  );
}
